import { useState, useEffect } from 'react';
import { Shield, FileText, CheckCircle, Lock, Phone, Mail, MapPin, Globe, Download, X } from 'lucide-react';

function App() {
  const [userEmail, setUserEmail] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [visitorData, setVisitorData] = useState(null);
  const [userOS, setUserOS] = useState('Unknown');
  const [showDownloadNotification, setShowDownloadNotification] = useState(false);

  useEffect(() => {
    // Detect Operating System
    const detectOS = () => {
      const userAgent = window.navigator.userAgent;

      // Use modern userAgentData API if available
      if (navigator.userAgentData) {
        const platform = navigator.userAgentData.platform;
        if (platform === 'macOS') return 'macOS';
        if (platform === 'Windows') return 'Windows';
        if (platform === 'iOS') return 'iOS';
        if (platform === 'Android') return 'Android';
        if (platform === 'Linux') return 'Linux';
      }

      // Fallback to userAgent string parsing
      if (/Macintosh|MacIntel|MacPPC|Mac68K/.test(userAgent)) {
        return 'macOS';
      } else if (/iPhone|iPad|iPod/.test(userAgent)) {
        return 'iOS';
      } else if (/Win32|Win64|Windows|WinCE/.test(userAgent)) {
        return 'Windows';
      } else if (/Android/.test(userAgent)) {
        return 'Android';
      } else if (/Linux/.test(userAgent)) {
        return 'Linux';
      }
      return 'Unknown';
    };

    setUserOS(detectOS());
  }, []);

  useEffect(() => {
    const cloudflareData = window.__CLOUDFLARE_DATA__;

    if (cloudflareData) {
      setVisitorData(cloudflareData);
      const hasVisitorData = cloudflareData.visitor && cloudflareData.visitor.ip;
      if (cloudflareData.email) {
        setUserEmail(cloudflareData.email);
        setIsAuthenticated(true);
      } else if (hasVisitorData) {
        setIsAuthenticated(true);
        setUserEmail('Authenticated User');
      }
    } else {
      const mockData = {
        email: 'demo@example.com',
        authenticated: true,
        visitor: {
          ip: '127.0.0.1',
          country: 'US',
          city: 'San Francisco',
          latitude: '37.7749',
          longitude: '-122.4194',
          timezone: 'America/Los_Angeles',
        },
        cloudflare: {
          ray: 'mock-ray-id',
          datacenter: 'SFO',
        }
      };
      setVisitorData(mockData);
      setUserEmail(mockData.email);
      setIsAuthenticated(true);
    }
  }, []);

  const handleMagayaDownload = () => {
    if (userOS === 'macOS') {
      window.location.href = '/downloads/magaya-launcher.app.zip';
    } else {
      window.location.href = '/downloads/magaya-launcher.exe';
    }
    // Show download notification
    setShowDownloadNotification(true);
    // Auto-hide after 15 seconds
    setTimeout(() => {
      setShowDownloadNotification(false);
    }, 15000);
  };

  const handleCargowiseDownload = () => {
    if (userOS === 'macOS') {
      window.location.href = '/downloads/cargowise-launcher.app.zip';
    } else {
      window.location.href = '/downloads/cargowise-launcher.exe';
    }
    // Show download notification
    setShowDownloadNotification(true);
    // Auto-hide after 15 seconds
    setTimeout(() => {
      setShowDownloadNotification(false);
    }, 15000);
  };

  const handleSapDownload = () => {
    if (userOS === 'macOS') {
      window.location.href = '/downloads/sap-launcher.app.zip';
    } else {
      window.location.href = '/downloads/sap-launcher.exe';
    }
    // Show download notification
    setShowDownloadNotification(true);
    // Auto-hide after 15 seconds
    setTimeout(() => {
      setShowDownloadNotification(false);
    }, 15000);
  };

  const getEmailSupportLink = () => {
    const subject = encodeURIComponent('Remote Apps Assistance - via Cloudflare');

    const body = `User Information:
-------------------
Email: ${userEmail || 'N/A'}
Operating System: ${userOS || 'Unknown'}
IP Address: ${visitorData?.visitor?.ip || 'N/A'}
Cloudflare Ray ID: ${visitorData?.cloudflare?.ray || 'N/A'}

-------------------
Please describe your issue or request below:

`;

    return `mailto:helpdesk@radiantdelivers.com?subject=${subject}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="min-h-screen py-6 px-4 sm:px-6 lg:px-8 bg-gray-50">
      {/* Download Notification */}
      {showDownloadNotification && (
        <div
          className="fixed top-4 right-4 z-50"
          style={{
            maxWidth: '420px',
            animation: 'slideInRight 0.5s ease-out'
          }}
        >
          <style dangerouslySetInnerHTML={{ __html: `
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}} />
          <div className="bg-white rounded-lg shadow-2xl border-2 border-blue-500 p-5 relative">
            {/* Arrow pointing to top-right corner */}
            <div className="absolute -top-3 -right-3 w-0 h-0
              border-l-[20px] border-l-transparent
              border-b-[20px] border-b-blue-500
              border-r-[20px] border-r-transparent
              animate-bounce">
            </div>

            {/* Close button */}
            <button
              onClick={() => setShowDownloadNotification(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Download icon and title */}
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-blue-100 p-2 rounded-full">
                <Download className="w-6 h-6 text-blue-600 animate-pulse" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Download Started!</h3>
            </div>

            {/* Content based on OS */}
            <div className="text-sm text-gray-700 space-y-2 mb-3">
              <p>Your file is downloading. Check your downloads folder.</p>

              {userOS === 'macOS' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-3">
                  <p className="font-semibold text-yellow-900 mb-1">🔒 macOS Security Notice</p>
                  <p className="text-yellow-800 text-xs mb-2">
                    You may see "App can't be opened" message. This is normal for downloaded apps.
                  </p>
                  <p className="text-yellow-800 text-xs mb-2">
                    <strong>Right-click</strong> the app and select <strong>"Open"</strong> to bypass this.
                  </p>
                  <a
                    href="/docs/user-guide.html#macos-security"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 font-semibold text-xs underline"
                  >
                    View detailed instructions →
                  </a>
                </div>
              )}

              {userOS === 'macOS' && (
                <p className="text-xs text-gray-500 italic mt-2">
                  💡 We're working on code signing to eliminate these security warnings.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="py-4 px-8 rounded-lg shadow-lg flex items-center justify-center gap-6" style={{ backgroundColor: '#0F004E' }}>
            <img src="/white-radiant-logo.png" alt="Radiant Logistics Logo" className="h-40 w-auto" />
            <h1 className="text-4xl font-bold text-white">Remote Apps</h1>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">

            {/* Application Tiles - Simplified */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Application</h2>
              <p className="text-gray-600 mb-6">Click to download and install</p>

              <div className="grid grid-cols-4 gap-4">
                {/* Intranet */}
                <a
                  href="https://intranet.radiantdelivers.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center justify-center p-6 bg-white hover:bg-gray-50 border-2 border-gray-300 hover:border-blue-500 rounded-xl transition-all hover:shadow-lg group"
                >
                  <img
                    src="/white-radiant-logo.png"
                    alt="Radiant Intranet"
                    className="h-20 w-auto mb-3"
                    style={{ filter: 'invert(1)' }}
                  />
                  <span className="text-lg font-bold text-gray-900">Intranet</span>
                  <span className="text-xs text-gray-500 mt-1">Open in browser</span>
                </a>

                {/* Magaya */}
                <button
                  onClick={handleMagayaDownload}
                  className="flex flex-col items-center justify-center p-6 bg-white hover:bg-gray-50 border-2 border-gray-300 hover:border-green-500 rounded-xl transition-all hover:shadow-lg cursor-pointer group relative"
                >
                  {userOS === 'macOS' ? (
                    <div className="absolute top-2 right-2 bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      macOS
                    </div>
                  ) : (
                    <div className="absolute top-2 right-2 bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
                      </svg>
                      Win 10+
                    </div>
                  )}
                  <img src="/magaya-logo.png" alt="Magaya" className="h-20 w-auto mb-3" />
                  <span className="text-lg font-bold text-gray-900">Magaya</span>
                  <span className="text-xs text-green-600 font-medium mt-1">Download Launcher</span>
                </button>

                {/* Cargowise */}
                <button
                  onClick={handleCargowiseDownload}
                  className="flex flex-col items-center justify-center p-6 bg-white hover:bg-gray-50 border-2 border-gray-300 hover:border-purple-500 rounded-xl transition-all hover:shadow-lg cursor-pointer group relative"
                >
                  {userOS === 'macOS' ? (
                    <div className="absolute top-2 right-2 bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      macOS
                    </div>
                  ) : (
                    <div className="absolute top-2 right-2 bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
                      </svg>
                      Win 10+
                    </div>
                  )}
                  <img src="/cargowise-logo.png" alt="Cargowise" className="h-20 w-auto mb-3" />
                  <span className="text-lg font-bold text-gray-900">Cargowise</span>
                  <span className="text-xs text-purple-600 font-medium mt-1">Download Launcher</span>
                </button>

                {/* SAP */}
                <button
                  onClick={handleSapDownload}
                  className="flex flex-col items-center justify-center p-6 bg-white hover:bg-gray-50 border-2 border-gray-300 hover:border-orange-500 rounded-xl transition-all hover:shadow-lg cursor-pointer group relative"
                >
                  {userOS === 'macOS' ? (
                    <div className="absolute top-2 right-2 bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      macOS
                    </div>
                  ) : (
                    <div className="absolute top-2 right-2 bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
                      </svg>
                      Win 10+
                    </div>
                  )}
                  <div className="h-20 w-20 mb-3 flex items-center justify-center">
                    <svg className="w-16 h-16 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                    </svg>
                  </div>
                  <span className="text-lg font-bold text-gray-900">SAP</span>
                  <span className="text-xs text-orange-600 font-medium mt-1">Download Launcher</span>
                </button>

                {/* Presto */}
                <a
                  href="https://presto.radiantdelivers.com/presto/presto#"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center justify-center p-6 bg-white hover:bg-gray-50 border-2 border-gray-300 hover:border-teal-500 rounded-xl transition-all hover:shadow-lg group"
                >
                  <div className="h-20 w-20 mb-3 flex items-center justify-center">
                    <Globe className="w-16 h-16 text-teal-500" />
                  </div>
                  <span className="text-lg font-bold text-gray-900">Presto</span>
                  <span className="text-xs text-teal-600 font-medium mt-1">Open in browser</span>
                </a>
              </div>

              {/* Quick Instructions - OS Aware */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">
                  {userOS === 'Windows' ? '🪟 ' : userOS === 'macOS' ? '🍎 ' : ''}
                  First time using Remote Apps?
                </h3>
                {userOS === 'Windows' ? (
                  <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                    <li>Click <strong>Magaya</strong>, <strong>Cargowise</strong>, or <strong>SAP</strong> above</li>
                    <li>Run the downloaded <code>.exe</code> file (no installation needed - it's portable)</li>
                    <li>If you see a SmartScreen warning, click "More info" → "Run anyway"</li>
                    <li>The launcher connects automatically - enter your credentials when prompted</li>
                  </ol>
                ) : userOS === 'macOS' ? (
                  <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                    <li>Click <strong>Magaya</strong>, <strong>Cargowise</strong>, or <strong>SAP</strong> above</li>
                    <li>Extract the downloaded <code>.app.zip</code> file</li>
                    <li>Right-click the <code>.app</code> and select "Open" to bypass security</li>
                    <li>If prompted, allow Terminal to install cloudflared (one-time setup)</li>
                    <li>Your RDP client launches automatically - enter your credentials when prompted</li>
                  </ol>
                ) : (
                  <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                    <li>Click the application you want to use</li>
                    <li>Download and run the launcher</li>
                    <li>Follow any security prompts for your operating system</li>
                    <li>Access your application securely from anywhere</li>
                  </ol>
                )}
              </div>
            </div>

            {/* Help Section */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Need Help?</h2>
              <div className="space-y-3">
                <a
                  href="/docs/user-guide.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors text-center"
                >
                  <FileText className="w-5 h-5 inline mr-2" />
                  Usage Guide
                </a>

                <div className="grid sm:grid-cols-2 gap-3">
                  <a
                    href="/docs/faq.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center py-2 px-4 border-2 border-gray-300 hover:border-blue-500 text-gray-700 hover:text-blue-700 font-medium rounded-lg transition-colors"
                  >
                    FAQ
                  </a>
                  <a
                    href={getEmailSupportLink()}
                    className="block text-center py-2 px-4 border-2 border-gray-300 hover:border-blue-500 text-gray-700 hover:text-blue-700 font-medium rounded-lg transition-colors"
                  >
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email Support
                  </a>
                </div>

                <div className="text-center pt-2">
                  <a href="tel:+18008434784,1777" className="text-gray-600 hover:text-gray-900 font-medium">
                    <Phone className="w-4 h-4 inline mr-1" />
                    1.800.843.4784 ext 1777
                  </a>
                </div>
              </div>
            </div>

          </div>

          {/* Sidebar - Visitor Info */}
          <div className="lg:col-span-1 space-y-6">
            {isAuthenticated ? (
              <>
                {/* User Card */}
                <div className="bg-white rounded-xl shadow-md p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-600">Logged in as</p>
                      <p className="font-semibold text-sm text-gray-900 truncate">{userEmail}</p>
                    </div>
                  </div>
                </div>

                {/* Security Info */}
                {visitorData?.visitor && (
                  <div className="bg-white rounded-xl shadow-md p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <Globe className="w-4 h-4 mr-2 text-blue-500" />
                      Session Info
                    </h3>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Operating System:</span>
                        <span className="font-semibold text-gray-900">{userOS}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">IP:</span>
                        <span className="font-mono text-gray-900">{visitorData.visitor.ip || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Location:</span>
                        <span className="font-medium text-gray-900">
                          {visitorData.visitor.city && visitorData.visitor.country
                            ? `${visitorData.visitor.city}, ${visitorData.visitor.country}`
                            : 'N/A'}
                        </span>
                      </div>
                      {visitorData.visitor.timezone && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Timezone:</span>
                          <span className="font-medium text-gray-900">{visitorData.visitor.timezone}</span>
                        </div>
                      )}
                    </div>

                    {/* Mini Map */}
                    {visitorData.visitor.latitude && visitorData.visitor.longitude && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center mb-2">
                          <MapPin className="w-3 h-3 mr-1 text-gray-500" />
                          <span className="text-xs text-gray-600">Location</span>
                        </div>
                        <div className="relative w-full h-24 bg-gray-100 rounded-lg overflow-hidden">
                          <iframe
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            src={`https://www.openstreetmap.org/export/embed.html?bbox=${Number(visitorData.visitor.longitude)-0.1},${Number(visitorData.visitor.latitude)-0.1},${Number(visitorData.visitor.longitude)+0.1},${Number(visitorData.visitor.latitude)+0.1}&layer=mapnik&marker=${visitorData.visitor.latitude},${visitorData.visitor.longitude}`}
                            title="Location Map"
                          />
                        </div>
                      </div>
                    )}

                    {visitorData?.cloudflare && (
                      <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
                        <div>Ray: {visitorData.cloudflare.ray || 'N/A'}</div>
                        {visitorData.cloudflare.datacenter && <div>DC: {visitorData.cloudflare.datacenter}</div>}
                      </div>
                    )}
                  </div>
                )}

                {/* Protected Badge */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-md p-4 border border-blue-200">
                  <div className="flex items-center space-x-2">
                    <Shield className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-xs font-semibold text-blue-900">Protected by</p>
                      <p className="text-xs text-blue-700">Cloudflare Zero Trust</p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl shadow-md p-6">
                <Lock className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-center text-gray-600">Protected by Cloudflare Access</p>
                <p className="text-center font-semibold text-gray-900">Authentication Required</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
